import React, { useState, useEffect } from 'react';
import { ArrowRight, Calendar, Tag, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';
import axios from 'axios';

const BlogSection = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    // Delay fetch to not block initial render
    const timer = setTimeout(async () => {
      try {
        const url = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${url}/api/blog`);
        setPosts(res.data.slice(0, 3));
      } catch (e) {
        // Silent fail
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  if (posts.length === 0) return null;

  const getField = (post, field) => post[`${field}_${language}`] || post[`${field}_de`];

  return (
    <section id="blog" className="py-24 lg:py-32 bg-white dark:bg-gray-900 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-mid-pink/5 rounded-full blur-hero" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <AnimateOnScroll animation="fade-up">
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-mid-pink/20 to-mid-purple/20 text-mid-pink px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-pink/20">
              <BookOpen size={14} />
              {t('blog.badge')}
            </span>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4">
              {t('blog.title')} <span className="gradient-text">{t('blog.titleHighlight')}</span>
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.2}>
            <p className="text-dark-grey dark:text-gray-400 text-lg max-w-2xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </AnimateOnScroll>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <AnimateOnScroll key={post.id} animation="fade-up" delay={0.1 * index}>
              <Link to={`/blog/${post.id}`} className="group block h-full">
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-grey/20 dark:border-gray-700 overflow-hidden card-hover h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&q=80'}
                      alt={getField(post, 'title')}
                      className="w-full h-full object-cover img-zoom"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-mid-grey dark:text-gray-500 flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'ar' ? 'ar-SA' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg text-black dark:text-white mb-2 group-hover:text-mid-purple transition-colors line-clamp-2">
                      {getField(post, 'title')}
                    </h3>
                    <p className="text-dark-grey dark:text-gray-400 text-sm leading-relaxed flex-1 line-clamp-3">
                      {getField(post, 'excerpt')}
                    </p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {post.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-grey/10 dark:bg-gray-700 rounded-full text-xs text-dark-grey dark:text-gray-400">
                            <Tag size={10} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-grey/10 dark:border-gray-700">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-mid-purple group-hover:gap-2 transition-all">
                        {t('blog.readMore')}
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </AnimateOnScroll>
          ))}
        </div>

        {posts.length >= 3 && (
          <AnimateOnScroll animation="fade-up" delay={0.3}>
            <div className="mt-12 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 bg-black dark:bg-white text-white dark:text-black px-8 py-4 rounded-full font-mono text-sm uppercase tracking-wider btn-lift shadow-lg"
              >
                {t('blog.viewAll')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </AnimateOnScroll>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
